'use client'

import { Button } from '@/General/Button'
import {
  useCreateCustomerReviewMutation,
  useHasPurchasedProductQuery,
} from '@/generated/graphql'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { useForm } from 'react-hook-form'
import { useStore } from '@/store'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useState } from 'react'
import { useAuth } from '@/Auth/useAuth'
import { useImpersonationReviewBypass } from '../../useImpersonationReviewBypass'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { PRODUCT_REVIEW_MAX_LENGTH } from '@/productReviews'

export const NewReviewDialog = ({
  isOpen,
  setIsOpen,
  onSuccess,
  productId,
  productName,
  title,
  introText,
}: NewReviewDialogProps) => {
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const user = useStore(state => state.user)
  const hydrated = useStore(state => state.hydrated)
  const { isAuthenticated } = useAuth()
  const isImpersonationReviewBypass = useImpersonationReviewBypass()

  const { data: hasPurchasedData, isLoading: isLoadingPurchased } =
    useHasPurchasedProductQuery(
      {
        productId: productId,
      },
      {
        enabled:
          isOpen &&
          hydrated &&
          isAuthenticated &&
          !!user?.id &&
          !!productId &&
          !isImpersonationReviewBypass,
      }
    )

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ReviewFormData>({
    defaultValues: {
      rating: 5,
      review: '',
    },
  })

  const { mutate: createCustomerReview, isPending } =
    useCreateCustomerReviewMutation()

  const onSubmit = (data: ReviewFormData) => {
    if (!productId || !user?.id) {
      return
    }

    setError(null)
    createCustomerReview(
      {
        productId: productId,
        rating: data.rating,
        review: data.review,
      },
      {
        onSuccess: () => {
          setIsSuccess(true)
          reset()
          setTimeout(() => {
            if (onSuccess) {
              onSuccess()
            }
          }, 100)
        },
        onError: (error: unknown) => {
          if (
            error instanceof Error &&
            error.message.includes('AUTH_NOT_AUTHORIZED')
          ) {
            setError('You must have purchased this product to write a review.')
          } else {
            setError(
              'An error occurred while submitting your review. Please try again.'
            )
          }
        },
      }
    )
  }

  const handleClose = () => {
    setIsOpen(false)
    setIsSuccess(false)
    setError(null)
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop
        className="fixed inset-0 bg-black/75"
        aria-hidden="true"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full justify-center p-4 items-center sm:p-0">
          <DialogPanel
            className={`relative transform overflow-hidden rounded-2xl bg-white shadow-2xl sm:my-8 sm:w-full ${
              isSuccess ? 'sm:max-w-md' : 'sm:max-w-2xl'
            }`}
          >
            {isSuccess ? (
              <div>
                <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 px-6 pb-6 pt-8">
                  <div className="flex flex-col items-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-lg">
                      <CheckCircleIcon
                        className="h-8 w-8 text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-4 text-center">
                      <DialogTitle
                        as="h3"
                        className="mb-1 text-xl font-bold text-gray-900"
                      >
                        Review Submitted
                      </DialogTitle>
                      <p className="font-medium text-green-700">
                        Thanks for helping fellow magicians
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-6 text-center">
                  <p className="mb-6 text-gray-600">
                    Thank you for your review! It will be visible once approved.
                  </p>
                  <Button
                    type="button"
                    onClick={handleClose}
                    color="green"
                    className="w-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : isLoadingPurchased && !isImpersonationReviewBypass ? (
              <div className="px-6 py-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-4">
                  <svg
                    className="h-6 w-6 text-gray-600 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <p className="text-gray-500">Checking purchase status...</p>
              </div>
            ) : !isImpersonationReviewBypass &&
              !hasPurchasedData?.hasPurchasedProduct ? (
              <div>
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    Purchase Required
                  </DialogTitle>
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="px-6 py-4">
                  <p className="text-gray-600">
                    In order to keep our reviews as accurate as possible, you
                    need to have purchase this product from Vanishing Inc.
                    before you can leave a review.
                  </p>
                </div>
                <div className="border-t border-gray-200 px-6 py-4">
                  <Button
                    onClick={handleClose}
                    color="green"
                    className="cursor-pointer w-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <DialogTitle
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      {title || `Review ${productName}`}
                    </DialogTitle>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="px-6 py-4">
                    {error && (
                      <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-lg">
                        {error}
                      </div>
                    )}
                    {introText && (
                      <p className="mb-4 text-gray-600">{introText}</p>
                    )}
                    <ErrorBox
                      errors={
                        Object.values(errors)
                          .map(error => error?.message)
                          .filter(Boolean) as string[]
                      }
                    />
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="rating"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Rating
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('rating', {
                              required: 'Rating is required',
                              valueAsNumber: true,
                            })}
                            id="rating"
                            name="rating"
                            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ${
                              errors.rating
                                ? 'ring-red-500 focus:ring-red-300'
                                : 'ring-gray-300 focus:ring-vi-gray'
                            } sm:text-sm sm:leading-6`}
                          >
                            <option value="1">Absolute Rubbish</option>
                            <option value="2">Below Standard</option>
                            <option value="3">Pretty Good</option>
                            <option value="4">Good</option>
                            <option value="5">Excellent</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="review"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Review
                        </label>
                        <div className="mt-1">
                          <textarea
                            {...register('review', {
                              required: 'Review is required',
                              minLength: {
                                value: 10,
                                message:
                                  'Review must be at least 10 characters long',
                              },
                              maxLength: {
                                value: PRODUCT_REVIEW_MAX_LENGTH,
                                message: `Review must not exceed ${PRODUCT_REVIEW_MAX_LENGTH} characters`,
                              },
                            })}
                            id="review"
                            name="review"
                            rows={4}
                            maxLength={PRODUCT_REVIEW_MAX_LENGTH}
                            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ${
                              errors.review
                                ? 'ring-red-500 focus:ring-red-300'
                                : 'ring-gray-300 focus:ring-vi-gray'
                            } sm:text-sm sm:leading-6`}
                          />
                        </div>
                        <div className="mt-2 text-sm text-gray-500">
                          <p>
                            • Earn 25 VI points for reviews over 150 characters
                          </p>
                          <p>
                            • All reviews are manually approved before being
                            published
                          </p>
                          <p>• English only please</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 px-6 py-4">
                    <Button
                      type="submit"
                      color="green"
                      className="cursor-pointer w-full"
                      disabled={isPending}
                    >
                      {isPending ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

type ReviewFormData = {
  rating: number
  review: string
}

interface NewReviewDialogProps {
  onSuccess: () => void
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  productId: number
  productName: string
  title?: string
  introText?: string
}
